import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import * as FaIcons from 'react-icons/fa';
import { servicesAPI, contentAPI } from '../services/api';
import { useBooking } from '../contexts/BookingContext';
import { useSiteInfo } from '../contexts/SiteInfoContext';
import ServiceCard from '../components/services/ServiceCard';
import HeroSection from '../components/layout/HeroSection';
import servicesHeroImage from '../assets/images/hero-main.jpg';
import './Services.css';

const {
  FaCheckCircle,
  FaArrowRight,
  FaCalendarAlt,
  FaClipboardCheck,
  FaThumbsUp,
  FaWrench,
  FaCarSide,
  FaAward,
  FaUsers,
  FaShieldAlt,
  FaDollarSign,
  FaCar,
  FaClock
} = FaIcons;

const unwrapList = (payload) => {
  if (!payload) {
    return [];
  }
  if (Array.isArray(payload)) {
    return payload;
  }
  if (Array.isArray(payload.results)) {
    return payload.results;
  }
  return [];
};

const getIconComponent = (iconName, fallback) => {
  if (iconName && FaIcons[iconName]) {
    return FaIcons[iconName];
  }
  if (fallback && FaIcons[fallback]) {
    return FaIcons[fallback];
  }
  return FaCheckCircle;
};

const serviceProcess = [
  {
    step: 1,
    icon: FaCalendarAlt,
    title: 'Book Your Appointment',
    description: 'Schedule online or call us to book a convenient time for your service.',
  },
  {
    step: 2,
    icon: FaClipboardCheck,
    title: 'Vehicle Inspection',
    description: 'Our technicians perform a comprehensive inspection and diagnostic check.',
  },
  {
    step: 3,
    icon: FaThumbsUp,
    title: 'Approval & Quote',
    description: 'We provide a detailed report and transparent quote before any work begins.',
  },
  {
    step: 4,
    icon: FaWrench,
    title: 'Expert Service',
    description: 'Experienced technicians complete the work using quality parts and equipment.',
  },
  {
    step: 5,
    icon: FaCarSide,
    title: 'Quality Check & Delivery',
    description: 'Final inspection ensures everything meets our standards before you pick up.',
  },
];

const whyChooseUs = [
  {
    icon: FaAward,
    title: 'Family-Owned Since 2017',
    description: 'Local business committed to honest service and building lasting relationships.',
  },
  {
    icon: FaShieldAlt,
    title: 'Quality Guaranteed',
    description: 'OEM-grade parts and meticulous workmanship backed by our service guarantee.',
  },
  {
    icon: FaDollarSign,
    title: 'Transparent Pricing',
    description: 'Clear quotes, itemized invoices, and approval required before additional work.',
  },
  {
    icon: FaUsers,
    title: 'Experienced Technicians',
    description: 'Skilled team trained across all makes, models, and fleet requirements.',
  },
  {
    icon: FaCar,
    title: 'All Makes & Models',
    description: 'From everyday vehicles to European marques, we service them all.',
  },
  {
    icon: FaClock,
    title: 'Efficient Turnaround',
    description: 'Smart scheduling and refined processes get you back on the road faster.',
  },
];

const Services = () => {
  const { openBookingModal } = useBooking();
  const { siteInfo } = useSiteInfo();

  const [services, setServices] = useState([]);
  const [servicesPage, setServicesPage] = useState(null);
  const [showAllServices, setShowAllServices] = useState(false);

  const handleSeeAllServices = () => {
    setShowAllServices(true);
  };

  useEffect(() => {
    let isMounted = true;

    const loadServices = async () => {
      try {
        const response = await servicesAPI.getAllServices();
        if (isMounted) {
          setServices(unwrapList(response.data));
        }
      } catch (error) {
        console.error('Error fetching services', error);
      }
    };

    const loadServicesPage = async () => {
      try {
        const response = await contentAPI.getPage('services');
        if (isMounted) {
          setServicesPage(response.data);
        }
      } catch (error) {
        if (error?.response?.status !== 404) {
          console.error('Failed to load services page copy', error);
        }
      }
    };

    loadServices();
    loadServicesPage();

    return () => {
      isMounted = false;
    };
  }, []);

  const heroEyebrow =
    siteInfo?.site_settings?.['services.hero_eyebrow'] || 'Complete Automotive Solutions';
  const heroTitle = servicesPage?.title || 'Our Services';

  const heroLead =
    siteInfo?.site_settings?.['services.hero_lead'] ||
    'From preventative maintenance to complex repairs, Automotive Care delivers transparent, high-quality workmanship for every make and model.';

  const heroSecondary =
    siteInfo?.site_settings?.['services.hero_secondary'] ||
    'Explore our service categories below or talk with our team to tailor a solution for your vehicle.';

  // Dynamic hero image with fallback to static
  const servicesHeroImageSrc = (servicesPage?.hero_image && servicesPage.hero_image.trim()) || servicesHeroImage;

  return (
    <>
      <Helmet>
        <title>Our Services - Automotive Care | Laverton North</title>
        <meta
          name="description"
          content="Explore the full range of automotive services available at Automotive Care including minor and major servicing, brakes, suspension, mechanical repairs, air-conditioning, tyres, and batteries."
        />
      </Helmet>

      <HeroSection
        badge={heroEyebrow}
        title={heroTitle}
        subtitle={heroLead}
        description={heroSecondary}
        backgroundImage={servicesHeroImageSrc}
        primaryButtonText="Book an appointment"
        primaryButtonAction={openBookingModal}
        secondaryButtonText="Request a call back"
        secondaryButtonLink="/contact"
      />

      {/* Our Signature Services Carousel */}
      <section className="section signature-services-section">
        <div className="container">
          <motion.div
            className="section-header text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2>Our Signature Services</h2>
            <p className="section-subtitle">
              Comprehensive automotive care tailored to your vehicle&apos;s needs.
            </p>
          </motion.div>

          <div className="services-grid">
            {services.length > 0 ? (
              (showAllServices ? services : services.slice(0, 6)).map((service, index) => {
                const Icon = getIconComponent(service.category?.icon, 'FaWrench');
                return (
                  <ServiceCard
                    key={service.id || service.slug}
                    category={service}
                    index={index}
                    onBookClick={openBookingModal}
                    Icon={Icon}
                  />
                );
              })
            ) : (
              <div className="no-services-message">
                <p>Loading services... Please contact us for service information.</p>
              </div>
            )}
          </div>

          {services.length > 6 && !showAllServices && (
            <div className="text-center" style={{ marginTop: '2rem' }}>
              <button type="button" onClick={handleSeeAllServices} className="btn btn-primary btn-lg">
                See All Services <FaArrowRight />
              </button>
            </div>
          )}
        </div>
      </section>

      {/* How Our Service Works */}
      <section className="section process-section bg-gray">
        <div className="container">
          <motion.div
            className="section-header text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2>How Our Service Works</h2>
            <p className="section-subtitle">
              A transparent, customer-focused process from start to finish
            </p>
          </motion.div>

          <div className="process-timeline">
            {serviceProcess.map((step, index) => {
              const Icon = step.icon;
              return (
                <motion.div
                  key={step.step}
                  className="process-step"
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.15, duration: 0.5 }}
                >
                  <div className="process-number">{step.step}</div>
                  <div className="process-icon">
                    <Icon />
                  </div>
                  <div className="process-content">
                    <h3>{step.title}</h3>
                    <p>{step.description}</p>
                  </div>
                  {index < serviceProcess.length - 1 && (
                    <div className="process-connector"></div>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Automotive Care */}
      <section className="section why-choose-section">
        <div className="container">
          <motion.div
            className="section-header text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2>Why Choose Automotive Care</h2>
            <p className="section-subtitle">
              Trusted by Laverton North drivers for quality, honesty, and exceptional service
            </p>
          </motion.div>

          <div className="why-choose-grid">
            {whyChooseUs.map((reason, index) => {
              const Icon = reason.icon;
              return (
                <motion.div
                  key={reason.title}
                  className="why-choose-card"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                  whileHover={{ translateY: -8 }}
                >
                  <div className="why-choose-icon">
                    <Icon />
                  </div>
                  <h3>{reason.title}</h3>
                  <p>{reason.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="section services-cta">
        <div className="container">
          <motion.div
            className="services-cta-card"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            <h2>Not sure which service you need?</h2>
            <p>
              Reach out to our technicians with your vehicle details and current concerns. We&apos;ll help you choose the best
              service and provide a transparent estimate before we begin.
            </p>
            <div className="services-cta-actions">
              <button type="button" className="btn btn-primary btn-lg" onClick={openBookingModal}>
                Contact Automotive Care
              </button>
              <Link to="/book" className="btn btn-outline btn-lg">
                Book online
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default Services;
